import React, { FC, MouseEvent } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Box, InputAdornment, Stack, TextField } from "@mui/material";
import Loader from "@components/Loader/Loader";
import { useGetPhotosQuery } from "@store/photoApi";
import Pictures from "@components/Pictures/Pictures";
import { useForm } from "react-hook-form";
import { useDebounceValue } from "@hooks/useDebounceValue";
import { useTranslation } from "react-i18next";
import { MmsOutlined } from "@mui/icons-material";
import TextIconButton from "@components/TextIconButton";
import { useNavigate } from "react-router-dom";
import { ActivityType } from "@models/activity-type";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

const PatientPhotos: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const patient = useSelector((state: RootState) => state.home.selectedPatient);
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const searchForm = useForm<{ search: string }>();
  const searchStr = useDebounceValue(searchForm.watch("search"), 500);
  const { data: photos, isLoading } = useGetPhotosQuery(
    {
      patientId: selectedPatientId!,
      search: searchStr,
    },
    {
      skip: !selectedPatientId,
    },
  );

  const navigateToTalk = (event: MouseEvent) => {
    event.stopPropagation();
    navigate(`/talk`, { state: { activityType: ActivityType.GallerySession } });
  };

  return (
    <Box display="flex" flexDirection="column">
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="32px"
      >
        <TextIconButton
          onClick={navigateToTalk}
          variant="contained"
          color="success"
          iconLeft={true}
          text={t("patientDashboard.albums.watchButton")}
          icon={MmsOutlined}
        />

        <TextField
          {...searchForm.register(`search`)}
          type="text"
          size="small"
          variant="outlined"
          placeholder={t("photos.searchInput.placeholder")}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>
      {!patient || isLoading ? (
        <Loader />
      ) : (
        <Pictures pictures={photos?.data || []} />
      )}
    </Box>
  );
};

export default PatientPhotos;
